import React, { useMemo, useState } from "react";
import { X } from "lucide-react";
import { useTheme } from "../ThemeProvider";
import { displayPhotoUrl } from "@/lib/photo";
import { getSeliseCSuiteColor, getCategoryColor, getThemeColor } from "./seliseColors";

const ROLE_LABELS = { VP: "Vice President" };

const CATEGORY_LABELS = {
  industry: "Industry",
  product: "Product",
  service: "Service",
  support: "Support",
};
const CATEGORY_ORDER = ["industry", "product", "service", "support"];

function MiniAvatar({ name, photoUrl, isDark }) {
  const { theme } = useTheme();
  const palette =
    theme === "selise" || theme === "dark"
      ? ["#1e3a8a", "#2563eb", "#3b82f6", "#1e293b", "#475569", "#64748b", "#334155", "#60a5fa"]
      : ["#007BFF", "#28A745", "#FF8C00", "#8A2BE2", "#E91E8C", "#17A2B8", "#FFC107", "#6C757D"];
  const getInitials = (n) => {
    const parts = n.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return n.substring(0, 2).toUpperCase();
  };
  const getColor = (n) => {
    const hash = n.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return palette[hash % palette.length];
  };
  const src = displayPhotoUrl(photoUrl);
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className="w-8 h-8 rounded-full object-cover flex-shrink-0 shadow-sm"
      />
    );
  }
  return (
    <div
      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 shadow-sm"
      style={{ backgroundColor: getColor(name || "?") }}
    >
      {getInitials(name || "?")}
    </div>
  );
}

export default function HierarchyModal({ nodes, onClose }) {
  const { theme } = useTheme();
  const [viewMode, setViewMode] = useState("executive");
  const [hover, setHover] = useState(null);
  const useSeliseColors = theme === "selise" || theme === "dark";
  const isDark = theme === "dark";

  const { columns, rows } = useMemo(() => {
    const divisions = nodes
      .filter((n) => n.node_type === "division")
      .sort((a, b) => {
        const ca = CATEGORY_ORDER.indexOf(a.category);
        const cb = CATEGORY_ORDER.indexOf(b.category);
        if (ca !== cb) return ca - cb;
        return (a.order || 0) - (b.order || 0);
      });

    if (viewMode === "executive") {
      const cSuite = nodes
        .filter((n) => n.node_type === "c_suite")
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      const grid = divisions.map((d) => {
        const people = nodes
          .filter((n) => n.node_type === "person" && n.parent_id === d.id)
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        const cells = cSuite.map((c) => ({
          exec: c,
          division: d,
          people: people.filter((p) => p.c_level_id === c.id),
        }));
        return { division: d, cells };
      });

      return { columns: cSuite, rows: grid };
    }

    // Functional leader view: columns are people referenced in others' collaborators
    const leaderIds = new Set();
    nodes
      .filter((n) => n.node_type === "person")
      .forEach((p) => {
        (p.collaborators || []).forEach((id) => leaderIds.add(id));
      });
    const leaders = nodes
      .filter((n) => leaderIds.has(n.id))
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    const grid = divisions.map((d) => {
      const people = nodes
        .filter((n) => n.node_type === "person" && n.parent_id === d.id)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      const cells = leaders.map((l) => ({
        exec: l,
        division: d,
        people: people.filter((p) => (p.collaborators || []).includes(l.id)),
      }));
      return { division: d, cells };
    });

    return { columns: leaders, rows: grid };
  }, [nodes, viewMode]);

  const execColor = (idx, node) =>
    getThemeColor(node?.color, theme, useSeliseColors ? getSeliseCSuiteColor(idx) : "#3465a4");

  const unitColor = (division) => getThemeColor(division.color, theme, getCategoryColor(division.category));

  // Convert hex to rgba with alpha for semi-transparent sticky backgrounds (enables backdrop-blur)
  const hexToRgba = (hex, alpha = 0.78) => {
    if (!hex) return `rgba(100,116,139,${alpha})`;
    const h = hex.replace("#", "");
    const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
    const r = parseInt(full.substring(0, 2), 16);
    const g = parseInt(full.substring(2, 4), 16);
    const b = parseInt(full.substring(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  };

  // Responsive sizing: minmax ensures fluid width, with clamps for min/max
  const COL_MIN = 200;
  const COL_MAX = 300;
  const ROW_MIN = 140;
  const ROW_MAX = 220;
  const LABEL_MIN = 180;
  const LABEL_MAX = 260;
  const HEADER_HEIGHT = 120;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-md"
      onClick={onClose}
    >
      <div
        className={`rounded-2xl shadow-2xl w-full max-w-[97vw] max-h-[92vh] flex flex-col overflow-hidden ring-1 ring-black/5 ${isDark ? "bg-card" : "bg-white"}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between gap-4 px-5 sm:px-7 py-4 border-b ${isDark ? "border-gray-700 bg-gradient-to-r from-gray-800 via-card to-gray-800" : "border-gray-100 bg-gradient-to-r from-slate-50 via-white to-blue-50/40"}`}>
          <div className="min-w-0">
            <h2 className={`text-base sm:text-lg font-bold tracking-tight ${isDark ? "text-gray-100" : "text-gray-900"}`}>
              {viewMode === "executive"
                ? "Executive × Unit Management Matrix"
                : "Functional Leader × Unit Management Matrix"}
            </h2>
            <p className={`text-xs mt-0.5 leading-relaxed ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              {viewMode === "executive"
                ? "Each cell represents the management team of a respective unit, with a direct reporting line to the Board of Executives."
                : "Each cell shows the people in a respective unit guided by a functional leader."}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className={`flex items-center gap-1 p-1 rounded-lg ${isDark ? "bg-gray-800" : "bg-gray-100"}`}>
              <button
                onClick={() => setViewMode("executive")}
                className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${
                  viewMode === "executive"
                    ? isDark ? "bg-card text-blue-400 shadow-sm" : "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Executive
              </button>
              <button
                onClick={() => setViewMode("functional")}
                className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${
                  viewMode === "functional"
                    ? isDark ? "bg-card text-blue-400 shadow-sm" : "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Functional Leader
              </button>
            </div>
            <button
              onClick={onClose}
              title="Close"
              className={`p-2 rounded-xl transition-colors ${isDark ? "hover:bg-gray-800 text-gray-400 hover:text-gray-200" : "hover:bg-gray-100 text-gray-500 hover:text-gray-700"}`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Matrix Canvas */}
        <div className={`flex-1 overflow-auto p-4 sm:p-6 ${isDark ? "bg-gradient-to-br from-gray-800 via-card to-gray-800" : "bg-gradient-to-br from-slate-50 via-white to-blue-50/20"}`}>
          {columns.length === 0 || rows.length === 0 ? (
            <div className={`h-full flex items-center justify-center text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>
              Not enough data to build the matrix.
            </div>
          ) : (
            <div
              className="grid gap-3 mx-auto"
              style={{
                gridTemplateColumns: `repeat(${columns.length + 1}, minmax(${COL_MIN}px, ${COL_MAX}px))`,
                gridTemplateRows: `${HEADER_HEIGHT}px repeat(${rows.length}, minmax(${ROW_MIN}px, ${ROW_MAX}px))`,
                width: "max-content",
                maxWidth: "100%",
              }}
            >
              {/* Top-left corner */}
              <div className="sticky top-0 left-0 z-30 bg-transparent" />

              {/* Column headers */}
              {columns.map((c, i) => (
                <div
                  key={c.id}
                  className="sticky top-0 z-20 flex items-end justify-center"
                >
                  <div
                    className={`w-full rounded-2xl overflow-hidden shadow-lg backdrop-blur-md ${isDark ? "border border-gray-700" : "border border-white/30"}`}
                    style={{ backgroundColor: hexToRgba(execColor(i, c), isDark ? 0.9 : 0.78) }}
                  >
                    <div className="flex flex-col items-center py-3 px-2">
                      <div
                        className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md overflow-hidden flex-shrink-0"
                        style={{ backgroundColor: execColor(i, c) }}
                      >
                        {c.photo_url ? (
                          <img
                            src={c.photo_url}
                            alt={c.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          c.name
                            ?.split(/\s+/)
                            .slice(0, 2)
                            .map((p) => p[0])
                            .join("")
                            .toUpperCase()
                        )}
                      </div>
                      <p className="text-white text-xs font-bold text-center mt-2 leading-tight px-1 break-words w-full">
                        {viewMode === "executive" ? c.title : c.name}
                      </p>
                      <p className="text-white/80 text-[10px] text-center mt-0.5 break-words w-full">
                        {viewMode === "executive"
                          ? c.name
                          : [ROLE_LABELS[c.role_level] || c.role_level, c.function_area].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {/* Rows */}
              {rows.map((row) => (
                <React.Fragment key={row.division.id}>
                  {/* Row header (unit) */}
                  <div
                    className="sticky left-0 z-10 flex items-center"
                  >
                    <div
                      className={`w-full rounded-2xl overflow-hidden shadow-lg ${isDark ? "border border-gray-700" : "border border-white/20"}`}
                      style={{ backgroundColor: unitColor(row.division) }}
                    >
                      <div className="px-3 py-3">
                        <p className="text-white text-sm font-bold leading-tight break-words">
                          {row.division.department || row.division.name}
                        </p>
                        <p className="text-white/80 text-[10px] uppercase tracking-wide mt-1 break-words">
                          {CATEGORY_LABELS[row.division.category] || row.division.category}
                          {row.division.department_code ? ` · ${row.division.department_code}` : ""}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Cells */}
                  {row.cells.map((cell, ci) => {
                    const isEmpty = cell.people.length === 0;
                    const isHovered =
                      hover &&
                      hover.exec === cell.exec.id &&
                      hover.unit === cell.division.id;
                    return (
                      <div
                        key={cell.exec.id}
                        className="flex items-center justify-center"
                        onMouseEnter={() =>
                          setHover({ exec: cell.exec.id, unit: cell.division.id })
                        }
                        onMouseLeave={() => setHover(null)}
                      >
                        <div
                          className={`w-full h-full rounded-2xl border backdrop-blur-md transition-all duration-200 flex flex-col ${
                            isEmpty
                              ? isDark
                                ? "border-gray-700/50 bg-gray-800/40"
                                : "border-gray-200/50 bg-gray-50/40"
                              : isHovered
                              ? isDark
                                ? "border-gray-500 bg-card/90 shadow-xl scale-[1.02]"
                                : "border-gray-400 bg-white/90 shadow-xl scale-[1.02]"
                              : isDark
                              ? "border-gray-700 bg-card/90 shadow-md hover:shadow-lg"
                              : "border-gray-200 bg-white/90 shadow-md hover:shadow-lg"
                          }`}
                          style={
                            !isEmpty
                              ? { borderLeft: `4px solid ${execColor(ci, cell.exec)}` }
                              : {}
                          }
                        >
                          {isEmpty ? (
                            <div className="flex-1 flex items-center justify-center">
                              <span className={`text-sm ${isDark ? "text-gray-600" : "text-gray-300"}`}>—</span>
                            </div>
                          ) : (
                            <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
                              {cell.people.map((p) => (
                                <div key={p.id} className="flex items-center gap-2.5">
                                  <MiniAvatar name={p.name} photoUrl={p.photo_url} isDark={isDark} />
                                  <div className="leading-snug min-w-0">
                                    <p className={`text-xs font-semibold break-words ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                                      {p.name}
                                    </p>
                                    <p className={`text-[10px] break-words ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                                      {[p.seniority, ROLE_LABELS[p.role_level] || p.role_level]
                                        .filter(Boolean)
                                        .join(" ") +
                                        (p.function_area ? `, ${p.function_area}` : "")}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}